import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Add } from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  getChecklistPerguntaById,
  putChecklistPergunta,
  putChecklistResposta,
} from "../../api/editChecklistApi";
import {
  ChecklistPergunta,
  ChecklistResposta,
} from "../../types/editChecklist";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";

type FormValues = {
  descricao_checklist_pergunta: string;
  obrigatorio_checklist_pergunta: boolean;
  ordem_checklist_pergunta: number;
};

const EditarPergunta: React.FC = () => {
  const { perguntaId } = useParams<{ perguntaId: string }>();
  const id = Number(perguntaId);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {} as FormValues,
  });

  const [pergunta, setPergunta] = useState<ChecklistPergunta | null>(null);
  const [respostas, setRespostas] = useState<ChecklistResposta[]>([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token") || "";
        const data = await getChecklistPerguntaById(token, id);
        setPergunta(data);

        reset({
          descricao_checklist_pergunta: data.descricao_checklist_pergunta ?? "",
          obrigatorio_checklist_pergunta: !!data.obrigatorio_checklist_pergunta,
          ordem_checklist_pergunta: data.ordem_checklist_pergunta ?? 0,
        });

        const orderedRespostas = [...(data.respostas || [])].sort(
          (a, b) =>
            (a.ordem_checklist_resposta ?? 0) -
            (b.ordem_checklist_resposta ?? 0)
        );
        setRespostas(orderedRespostas);
      } catch (err) {
        console.error(err);
        toast.error("Erro ao buscar pergunta");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, reset]);

  const onSubmit = async (form: FormValues) => {
    try {
      const token = localStorage.getItem("token") || "";
      const payload = {
        ...form,
        ordem_checklist_pergunta: Number(form.ordem_checklist_pergunta),
      };

      await putChecklistPergunta(token, id, payload);
      toast.success("Pergunta atualizada");
      navigate(-1);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao atualizar pergunta");
    }
  };

  const handleDragEndRespostas = async (result: DropResult) => {
    if (!result.destination) return;

    const newRespostas = Array.from(respostas);
    const [moved] = newRespostas.splice(result.source.index, 1);
    newRespostas.splice(result.destination.index, 0, moved);

    const updated = newRespostas.map((r, index) => ({
      ...r,
      ordem_checklist_resposta: index + 1,
    }));

    setRespostas(updated);

    try {
      const token = localStorage.getItem("token") || "";
      await Promise.all(
        updated.map((r) =>
          putChecklistResposta(token, r.id, {
            ordem_checklist_resposta: r.ordem_checklist_resposta,
          })
        )
      );
      toast.success("Ordem das respostas atualizada!");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar nova ordem das respostas");
    }
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Typography variant="h5">Editar Pergunta #{id}</Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="descricao_checklist_pergunta"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Descrição da Pergunta"
              fullWidth
              margin="normal"
            />
          )}
        />

        <Controller
          name="ordem_checklist_pergunta"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Ordem da Pergunta"
              type="number"
              fullWidth
              margin="normal"
            />
          )}
        />

        <Controller
          name="obrigatorio_checklist_pergunta"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label="Obrigatória"
            />
          )}
        />

        <Box mt={2} display="flex" gap={2}>
          <Button variant="contained" type="submit">
            Salvar Pergunta
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Voltar
          </Button>
        </Box>
      </form>

      <Box mt={4}>
        <Box
          mb={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6">Respostas (arraste para ordenar)</Typography>

          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() =>
              navigate(`/editarChecklist/pergunta/${id}/resposta/novo`)
            }
          >
            Adicionar resposta
          </Button>
        </Box>

        <DragDropContext onDragEnd={handleDragEndRespostas}>
          <Droppable droppableId="respostas-list">
            {(provided) => (
              <List
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{ width: "100%", maxWidth: 800 }}
              >
                {respostas.map((r, index) => (
                  <Draggable
                    key={r.id.toString()}
                    draggableId={r.id.toString()}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          mb: 1,
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          background: snapshot.isDragging ? "#f0f0f0" : "white",
                        }}
                      >
                        <Box
                          {...provided.dragHandleProps}
                          sx={{
                            pr: 2,
                            display: "flex",
                            alignItems: "center",
                            color: "gray",
                            cursor: "grab",
                          }}
                        >
                          <DragIndicatorIcon />
                        </Box>
                        <ListItem
                          sx={{ width: "100%" }}
                          secondaryAction={
                            <IconButton
                              onClick={() =>
                                navigate(`/editarChecklist/resposta/${r.id}`)
                              }
                            >
                              <EditIcon />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={`${r.ordem_checklist_resposta}. ${r.descricao_checklist_resposta}`}
                            secondary={`Valor: ${r.valor_checklist_respostas}`}
                          />
                        </ListItem>
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </Box>
  );
};

export default EditarPergunta;
